import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { RefreshCw, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${API_BASE_URL}/api`;

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}j`;
};

const WebhookDeliveryLogs = ({ open, onClose, webhook, workspaceId }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [retrying, setRetrying] = useState(null);

  useEffect(() => {
    if (open && webhook) fetchDeliveries();
  }, [open, webhook]);

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/webhooks/${webhook._id}/deliveries`,
        { withCredentials: true }
      );
      setDeliveries(res.data);
    } catch {
      toast({ title: 'Erreur chargement des logs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (deliveryId) => {
    try {
      setRetrying(deliveryId);
      await axios.post(
        `${API}/webhooks/deliveries/${deliveryId}/retry`,
        {},
        { withCredentials: true }
      );
      toast({ title: 'Retry envoyé' });
      setTimeout(fetchDeliveries, 1500);
    } catch {
      toast({ title: 'Erreur lors du retry', variant: 'destructive' });
    } finally {
      setRetrying(null);
    }
  };

  const statusBadge = (status) => {
    if (status === 'success') return <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">✓ Succès</Badge>;
    if (status === 'failed') return <Badge variant="destructive">✗ Échec</Badge>;
    return <Badge variant="secondary">En cours</Badge>;
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center justify-between">
            <span>Logs — {webhook?.name}</span>
            <Button variant="ghost" size="sm" onClick={fetchDeliveries} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </SheetTitle>
        </SheetHeader>

        {loading && deliveries.length === 0 && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}

        {!loading && deliveries.length === 0 && (
          <p className="text-center text-gray-500 py-12 text-sm">Aucune livraison pour le moment.</p>
        )}

        <div className="space-y-1">
          {deliveries.map(d => (
            <div key={d._id} className="border rounded-lg overflow-hidden dark:border-gray-700">
              <button
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                onClick={() => setExpandedId(expandedId === d._id ? null : d._id)}
              >
                {expandedId === d._id
                  ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  : <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />}
                <span className="text-gray-400 w-10 shrink-0">{timeAgo(d.createdAt)}</span>
                <span className="font-mono text-xs text-blue-600 dark:text-blue-400 w-36 shrink-0">{d.event}</span>
                {statusBadge(d.status)}
                {d.http_status_code && (
                  <span className="ml-auto text-gray-500 text-xs">{d.http_status_code}</span>
                )}
                {d.duration_ms && (
                  <span className="text-gray-400 text-xs ml-2">{d.duration_ms}ms</span>
                )}
              </button>

              {expandedId === d._id && (
                <div className="border-t dark:border-gray-700 px-3 py-3 space-y-3 bg-gray-50 dark:bg-gray-900">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Payload envoyé</p>
                    <pre className="text-xs bg-white dark:bg-gray-800 rounded p-2 overflow-x-auto max-h-48 border dark:border-gray-700">
                      {JSON.stringify(d.payload, null, 2)}
                    </pre>
                  </div>
                  {d.response_body && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Réponse</p>
                      <pre className="text-xs bg-white dark:bg-gray-800 rounded p-2 overflow-x-auto max-h-32 border dark:border-gray-700">
                        {d.response_body}
                      </pre>
                    </div>
                  )}
                  {d.status === 'failed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRetry(d._id)}
                      disabled={retrying === d._id}
                    >
                      {retrying === d._id
                        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                      Réessayer
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default WebhookDeliveryLogs;
